import React, {useEffect, useRef, useState} from 'react';
import useAxiosProtect from '../../../../hooks/useAxiosProtect';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import OutlinedInput from '@mui/material/OutlinedInput';
import Form from 'react-bootstrap/Form';

const AccountManagementModal = props => {
    const axiosProtect = useAxiosProtect();
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    const [createForm, setCreateForm] = useState({
        firstName: '',
        firstNameError: '',
        lastName: '',
        lastNameError: '',
        email: '',
        emailError: '',
        role: '',
        roleError: '',
        org: null,
        orgError: '',
        sites: [],
        sitesError: ''
    });
    const [createConfirmation, setCreateConfirmation] = useState(false);

    const [editForm, setEditForm] = useState({
        firstName: '',
        firstNameError: '',
        lastName: '',
        lastNameError: '',
        email: '',
        emailError: '',
        role: '',
        roleError: '',
        sites: [],
        sitesError: ''
    });

    const [serverError, setServerError] = useState('');

    const [orgsLoading, setOrgsLoading] = useState(false);
    const [sitesLoading, setSitesLoading] = useState(false);

    const [orgList, setOrgList] = useState([]);
    const [siteList, setSiteList] = useState([]);

    const [allSelected, setAllSelected] = useState(false);

    const [upgradeAccount, setUpgradeAccount] = useState(false);

    const updateFormField = (field, value) => {
        if(props.modalState === 'create'){
            if(field === 'org'){
                setCreateForm(prev => ({
                    ...prev,
                    [`${field}`]: value,
                    'sites': []
                }));
            }else{
                setCreateForm(prev => ({
                    ...prev,
                    [`${field}`]: value
                }));
            }

            if(field === 'role' && value === 'corporate' && props.auth.role === process.env.REACT_APP_CLIENT_ADMIN) setOrgsLoading(true);
        }else if(props.modalState === 'edit'){
            setEditForm(prev => ({
                ...prev,
                [`${field}`]: value
            }));
        }
    }

    const generateTitle = () => {
        if(props.modalState === 'create') return 'Create';
        if(props.modalState === 'edit') return 'Edit';
        if(props.modalState === 'delete') return 'Delete';

        return '';
    }

    useEffect(() => {
        if(props.modalState === ''){
            setCreateForm({
                firstName: '',
                firstNameError: '',
                lastName: '',
                lastNameError: '',
                email: '',
                emailError: '',
                role: '',
                roleError: '',
                org: null,
                orgError: '',
                sites: [],
                sitesError: ''
            });

            setCreateConfirmation(false);

            setEditForm({
                firstName: '',
                firstNameError: '',
                lastName: '',
                lastNameError: '',
                email: '',
                emailError: '',
                role: '',
                roleError: '',
                sites: [],
                sitesError: ''
            });

            setUpgradeAccount(false);
        }
    }, [props.modalState]);

    useEffect(() => {
        if(createForm.role === 'staff' || (props.auth.role === process.env.REACT_APP_ES_ADMIN && createForm.role === 'corporate')){
            setOrgsLoading(true);
        }
    }, [createForm.role]);

    useEffect(() => {
        if(createForm.org == null) return;
        setSitesLoading(true);
    }, [createForm.org]);

    useEffect(() => {
        if(orgsLoading){
            axiosProtect.get('/auth/query/list-all-orgs').then(response => {
                setOrgList(response.data.orgs);

                if(props.auth.role === process.env.REACT_APP_CLIENT_ADMIN){
                    for(let i = 0;i<response.data.orgs.length;++i){
                        if(props.auth.org_id === response.data.orgs[i]._id){
                            updateFormField('org', response.data.orgs[i]);
                            break;
                        }
                    }
                }

                setOrgsLoading(false);
            }).catch(error => {
                setOrgsLoading(false);
            });
        }

        if(sitesLoading){
            axiosProtect.get(`/auth/query/list-org-sites?orgID=${createForm.org._id}`).then(response => {
                setSiteList(response.data.sites);
                setSitesLoading(false);
            }).catch(error => {
                setSitesLoading(false);
            });
        }
    }, [orgsLoading, sitesLoading]);

    useEffect(() => {
        if(props.userToDelete != null) props.setModalState('delete');
        else if(props.userToEdit != null){
            props.setModalState('edit');

            axiosProtect.get('/auth/query/list-all-orgs').then(response => {
                setOrgList(response.data.orgs);

                let queryOrg = {};
                for(let i = 0;i<response.data.orgs.length;++i){
                    if(response.data.orgs[i]._id === props.userToEdit.org_id){
                        queryOrg = response.data.orgs[i];
                        break;
                    }
                }

                axiosProtect.get(`/auth/query/list-org-sites?orgName=${queryOrg.name}`).then(response => {
                    setSiteList(response.data.sites);
                    setEditForm(prev => ({
                        ...prev,
                        firstName: props.userToEdit.first_name,
                        lastName: props.userToEdit.last_name,
                        email: props.userToEdit.email,
                        sites: props.userToEdit.role === 'Staff user' && Array.isArray(props.userToEdit.sites) ? props.userToEdit.sites : '',
                    }));

                    setSitesLoading(false);
                }).catch(error => {
                    setSitesLoading(false);
                });
            }).catch(error => {
                setOrgsLoading(false);
            });
        }
    }, [props.userToEdit, props.userToDelete]);

    const SELECT_ALL = 'select-all-trigger';

    const handleSiteSelection = e => {
        const raw = e.target.value;
        const nextRaw = Array.isArray(raw) ? raw : raw.split(',');

        const allNames = siteList.map(s => s.name);
        const firstName = allNames[0] || null;

        const userClickedSelectAllRow = nextRaw.includes(SELECT_ALL);

        let nextNames = [];

        if(userClickedSelectAllRow){
            if(!allSelected){
                nextNames = allNames;
                setAllSelected(true);
            } else {
                nextNames = firstName ? [firstName] : [];
                setAllSelected(false);
            }
        } else {
            const cleaned = nextRaw.filter(v => v !== SELECT_ALL);

            nextNames = cleaned.length ? cleaned : (firstName ? [firstName] : []);

            setAllSelected(nextNames.length === allNames.length);
        }

        // const siteIds = sites.filter(s => nextNames.includes(s.name)).map(s => s.id);

        updateFormField('sites', nextNames);
    }

    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;

    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            },
        },
        disablePortal: true
    };

    const validateCreateForm = () => {
        let tempForm = {...createForm};
        let isValid = true;
        
        if(tempForm.firstName.trim() === ''){
            tempForm.firstNameError = 'Required';
            isValid = false;
        }else{
            tempForm.firstNameError = '';
            tempForm.firstName = tempForm.firstName.trim();
        }

        if(tempForm.lastName.trim() === ''){
            tempForm.lastNameError = 'Required';
            isValid = false;
        }else{
            tempForm.lastNameError = '';
            tempForm.lastName = tempForm.lastName.trim();
        }

        if(tempForm.email.trim() === ''){
            tempForm.emailError = 'Required';
            isValid = false;
        }else if(!emailRegex.test(tempForm.email.trim())){
            tempForm.emailError = 'Must be an email address';
            isValid = false;
        }else{
            tempForm.emailError = '';
            tempForm.email = tempForm.email.trim();
        }

        if(tempForm.role === ''){
            tempForm.roleError = 'Required';
            isValid = false;
        }else{
            tempForm.roleError = '';
        }

        if(tempForm.org == null && tempForm.role !== 'es-admin'){
            tempForm.orgError = 'Required';
            isValid = false;
        }else{
            tempForm.orgError = '';
        }

        if(tempForm.role === 'staff' && tempForm.sites.length === 0){
            tempForm.sitesError = 'User must have access to at least one site';
            isValid = false;
        }else{
            tempForm.sitesError = '';
        }

        setCreateForm(tempForm);
        return isValid;
    }

    const validateEditForm = () => {
        let tempForm = {...editForm};
        let isValid = true;
        
        if(tempForm.firstName.trim() === ''){
            tempForm.firstNameError = 'Required';
            isValid = false;
        }else{
            tempForm.firstNameError = '';
            tempForm.firstName = tempForm.firstName.trim();
        }

        if(tempForm.lastName.trim() === ''){
            tempForm.lastNameError = 'Required';
            isValid = false;
        }else{
            tempForm.lastNameError = '';
            tempForm.lastName = tempForm.lastName.trim();
        }

        if(tempForm.email.trim() === ''){
            tempForm.emailError = 'Required';
            isValid = false;
        }else if(!emailRegex.test(tempForm.email.trim())){
            tempForm.emailError = 'Must be an email address';
            isValid = false;
        }else{
            tempForm.emailError = '';
            tempForm.email = tempForm.email.trim();
        }

        setEditForm(tempForm);
        return isValid;
    }

    const translateRoleString = () => {
        if(createForm.role === 'es-admin') return parseInt(process.env.REACT_APP_ES_ADMIN);
        else if(createForm.role === 'corporate') return parseInt(process.env.REACT_APP_CLIENT_ADMIN);
        else if(createForm.role === 'staff') return parseInt(process.env.REACT_APP_CLIENT_STAFF);

        return -1;
    }

    const generateSiteString = () => {
        const nameToId = new Map(siteList.map(s => [s.name, s.id]));

        let correctForm = {...(props.modalState === 'edit' ? editForm : createForm)};
        const ids = correctForm.sites.map(name => nameToId.get(name));

        return ids.join(',');
    }

    const createNewUser = () => {
        if(!validateCreateForm()) return;

        let payload = {
            first_name: createForm.firstName,
            last_name: createForm.lastName,
            email: createForm.email,
            role: translateRoleString(),
            site_id: createForm.role === 'staff' ? generateSiteString() : '',
            isESAdmin: createForm.role === 'es-admin'
        }

        if(createForm.role !== 'es-admin') payload.org_id = createForm.org._id

        axiosProtect.post('/auth/register/auto-register', payload).then(response => {
            props.setUsers(prev => [...prev, response.data.createdUser]);
            setCreateConfirmation(true);
        }).catch(error => {
            setServerError(error.response.data);
        });
    }

    const updateUser = () => {
        if(!validateEditForm()) return;

        let payload = {
            uid: props.userToEdit._id,
            first_name: editForm.firstName,
            last_name: editForm.lastName,
            email: editForm.email,
            org: props.userToEdit.org === undefined ? props.auth.org_id: props.userToEdit.org,
            site_ids: props.userToEdit?.role === 'Staff user' && Array.isArray(props.userToEdit?.sites) ? generateSiteString() : null,
            upgradeAccount
        };

        axiosProtect.put('/auth/query/edit-user', payload).then(response => {
            props.setUsers(prev =>
                prev.map(u => (u._id === response.data.updatedUser._id ? response.data.updatedUser : u))
            );

            props.closeModal();
        }).catch(error => {
            setServerError(error.response.data.error);
        });
    }

    const deleteUser = () => {
        axiosProtect.delete(`/auth/query/delete-user?id=${props.userToDelete._id}`).then(response => {
            props.setUsers(prev => prev.filter(user => user._id !== response.data.deletedUID));
            props.closeModal();
        }).catch(error => {
            props.closeModal();
        });
    }

    const resetCreateForm = () => {
        setCreateForm({
            firstName: '',
            firstNameError: '',
            lastName: '',
            lastNameError: '',
            email: '',
            emailError: '',
            role: '',
            roleError: '',
            org: null,
            orgError: '',
            sites: [],
            sitesError: ''
        });

        setCreateConfirmation(false);
    }
    
    return (
        <Modal
            show={props.modalState !== ''}
            onHide={() => props.closeModal()}
            backdrop="static"
            keyboard={false}
            centered
            className="opp-modal-root"
            backdropClassName="opp-modal-backdrop"
            dialogClassName="opp-modal-cw"
        >
            <Modal.Header closeButton>
                {props.modalState !== '' && <Modal.Title>{generateTitle()} user</Modal.Title>}
            </Modal.Header>
            <Modal.Body>
                {
                    ((props.modalState === 'create' || props.modalState === 'edit') && !createConfirmation)
                    &&
                    <>
                    {serverError !== '' && <p style={{color:'red', textAlign:'center'}}>{serverError}</p>}

                    <TextField
                        label="First Name"
                        fullWidth
                        value={props.modalState === 'create' ? createForm.firstName : editForm.firstName}
                        onChange={e => updateFormField('firstName', e.target.value)}
                        inputProps={{ autoComplete: 'off' }}
                        error={props.modalState === 'create' ? createForm.firstNameError !== '' : editForm.firstNameError !== ''}
                        helperText={props.modalState === 'create' ? createForm.firstNameError : editForm.firstNameError}
                    />

                    <br/>
                    <br/>

                    <TextField
                        label="Last Name"
                        fullWidth
                        value={props.modalState === 'create' ? createForm.lastName : editForm.lastName}
                        onChange={e => updateFormField('lastName', e.target.value)}
                        inputProps={{ autoComplete: 'off' }}
                        error={props.modalState === 'create' ? createForm.lastNameError !== '' : editForm.lastNameError !== ''}
                        helperText={props.modalState === 'create' ? createForm.lastNameError : editForm.lastNameError}
                    />

                    <br/>
                    <br/>

                    <TextField
                        label="Email"
                        fullWidth
                        value={props.modalState === 'create' ? createForm.email : editForm.email}
                        onChange={e => updateFormField('email', e.target.value)}
                        inputProps={{ autoComplete: 'off' }}
                        error={props.modalState === 'create' ? createForm.emailError !== '' : editForm.emailError !== ''}
                        helperText={props.modalState === 'create' ? createForm.emailError : editForm.emailError}
                    />

                    {
                        (
                            props.auth.role === process.env.REACT_APP_CLIENT_ADMIN && 
                            props.modalState === 'edit' && 
                            props.userToEdit.role === 'Staff user'
                        )
                        &&
                        <>
                        <br/>
                        <br/>
                        <Form.Check 
                            type='checkbox'
                            id={`default-checkbox`}
                            label='Upgrade account to admin'
                            checked={upgradeAccount}
                            onClick={() => setUpgradeAccount(prev => !prev)}
                            className='data-checkbox'
                        />
                        <br/>
                        {upgradeAccount && <small>NOTE: this will give the user access to all sites within your organization</small>}
                        </>
                    }

                    {
                        props.modalState === 'create' &&
                        <>
                            <br/>
                            <br/>
                            <FormControl fullWidth>
                            <InputLabel id="demo-simple-select-label">Role</InputLabel>
                                <Select
                                    label="Role"
                                    MenuProps={{ disablePortal: true }}
                                    value={props.modalState === 'create' ? createForm.role : editForm.role}
                                    onChange={e => updateFormField('role', e.target.value)}
                                    error={props.modalState === 'create' ? createForm.roleError !== '' : editForm.roleError !== ''}
                                >
                                    {props.auth.role === process.env.REACT_APP_ES_ADMIN && <MenuItem value='es-admin'>Enviro-Stewards admin</MenuItem>}
                                    <MenuItem value='corporate'>Corporate user</MenuItem>
                                    <MenuItem value='staff'>Staff member</MenuItem>
                                </Select>
                                {
                                    (
                                        (props.modalState === 'create' && createForm.roleError !== '') || 
                                        (props.modalState === 'edit' && editForm.roleError !== '')
                                    ) && 
                                    <small className="text-error">{props.modalState === 'create' ? createForm.roleError : editForm.role}</small>
                                }
                            </FormControl>
                        </>
                    }

                    {
                        orgsLoading &&

                        <div style={{width:'100%'}}>
                            <Box style={{width:'100%', textAlign:'center', paddingTop:'2vh'}}>
                                <CircularProgress />
                            </Box>
                        </div>
                    }

                    {
                        (props.auth.role === process.env.REACT_APP_ES_ADMIN && props.modalState === 'create' && ['corporate', 'staff'].includes(createForm.role) && !orgsLoading) && 
                        <>
                        <Autocomplete
                            disablePortal
                            style={{width:'100%', margin:'2.5vh 0 1.25vh 0'}}
                            options={orgList}
                            value={createForm.org}
                            getOptionLabel={option => `${option.name}`}
                            onChange={(_, option) => {
                                updateFormField('org', option)
                            }}
                            renderOption={(props, option) => (
                                <Box component="li" {...props}>
                                    <span>{`${option.name}`}</span>
                                </Box>
                            )}
                            renderInput={(params) => <TextField {...params} inputProps={{...params.inputProps}} label="Organization" />}
                        />
                        {
                            (
                                (props.modalState === 'create' && createForm.orgError !== '') || 
                                (props.modalState === 'edit' && editForm.orgError !== '')
                            ) && 
                            <small className="text-error">{props.modalState === 'create' ? createForm.orgError : editForm.orgError}</small>
                        }
                        </>
                    }

                    {
                        sitesLoading &&
                        <div style={{width:'100%'}}>
                            <Box style={{width:'100%', textAlign:'center', paddingTop:'2vh'}}>
                                <CircularProgress />
                            </Box>
                        </div>
                    }

                    {
                        (
                            (
                                (props.modalState === 'create' && createForm.role === 'staff' && createForm.org != null && !sitesLoading) ||
                                (props.modalState === 'edit' && props.userToEdit?.role === 'Staff user' && Array.isArray(props.userToEdit?.sites)) && 
                                !upgradeAccount
                            ) 
                        )
                        &&
                        <FormControl fullWidth sx={{marginTop:'1.25vh'}}>
                            <InputLabel id="demo-multiple-checkbox-label">Sites</InputLabel>
                            <Select
                                labelId="demo-multiple-checkbox-label"
                                id="demo-multiple-checkbox"
                                multiple
                                error={props.modalState === 'create' ? createForm.sitesError !== '' : editForm.sitesError !== ''}
                                value={props.modalState === 'create' ? createForm.sites : editForm.sites}
                                onChange={handleSiteSelection}
                                input={<OutlinedInput label="Sites" />}
                                MenuProps={MenuProps}
                                renderValue={(selected) => (
                                    <Box sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {selected.filter(v => v !== 'select-all-trigger').join(', ')}
                                    </Box>
                                )}
                            >
                                {
                                    siteList.length > 0 &&
                                    <MenuItem key={'select-all'} value={'select-all-trigger'}>
                                        <Checkbox checked={allSelected} />
                                        <ListItemText primary={'-- SELECT ALL --'} />
                                    </MenuItem>
                                }
                                {siteList.map(site => (
                                    <MenuItem key={site.id} value={site.name}>
                                        <Checkbox checked={
                                                props.modalState === 'create' ? 
                                                createForm.sites.includes(site.name) :
                                                editForm.sites.includes(site.name)
                                            } 
                                        />
                                        <ListItemText primary={site.name} />
                                    </MenuItem>
                                ))}
                            </Select>
                            {
                                (
                                    (props.modalState === 'create' && createForm.sitesError !== '') || 
                                    (props.modalState === 'edit' && editForm.sitesError !== '')
                                ) && 
                                <small className="text-error">{props.modalState === 'create' ? createForm.sitesError : editForm.sitesError}</small>
                            }
                        </FormControl>
                    }
                    </>
                }
                {
                    createConfirmation &&
                    <p>An onboarding email will be sent <strong>{createForm.email}</strong> shortly.</p>
                }

                {
                    props.modalState === 'delete' &&
                    <p>Are you sure you want to delete {props.userToDelete.first_name} {props.userToDelete.last_name} ({props.userToDelete.email})?</p>
                }
            </Modal.Body>
            <Modal.Footer>
                {
                    !createConfirmation ?
                    <Button variant={props.modalState === 'delete' ? 'delete' : 'primary'} 
                        style={{backgroundColor:`${props.modalState === 'delete' ? 'rgb(213,43,0)' : 'rgb(8,114,182)'}`}}
                        onClick={() => {
                            if(props.modalState === 'create'){
                                createNewUser();
                            }else if(props.modalState === 'edit'){
                                updateUser();
                            }else if(props.modalState === 'delete'){
                                deleteUser();
                            }
                        }}
                    >
                        {
                            <span style={{color:'white'}}>
                                {props.modalState === 'create' ? 'Create' : props.modalState === 'edit' ? 'Update' : 'Delete'}
                            </span>
                        }
                    </Button>
                    :
                    <div style={{display:'flex', flexDirection:'row', width:'100%'}}>
                        <Button variant='secondary' onClick={() => props.closeModal()}>
                            Close
                        </Button>

                        <div style={{flex:'1'}}></div>

                        <Button onClick={() => resetCreateForm()} className="btn-brand-bg">
                            Create another
                        </Button>
                    </div>
                }
            </Modal.Footer>
        </Modal>
    );
}

export default AccountManagementModal;